'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useState } from 'react';
import { SpartanProductHeroBannerEntranceContext } from '@/app/lcv/components/sections/spartan/products/hero-banner/entrance/context';
import type { SpartanProductHeroBannerEntranceProps } from '@/app/lcv/components/sections/spartan/products/hero-banner/entrance/types';
import { useHeroEntranceReady } from '@/app/lcv/components/sections/spartan/products/hero-banner/entrance/useHeroEntranceReady';
import { SpartanProductHeroBannerMainBackground } from '@/app/lcv/components/sections/spartan/products/hero-banner/media/main-background';
import { SpartanProductHeroBannerParallaxScene } from '@/app/lcv/components/sections/spartan/products/hero-banner/media/parallax-scene';
import styles from '@/app/lcv/components/sections/spartan/products/hero-banner/media/parallax-scene/layout.module.css';
import { SpartanProductHeroBannerOverlays } from '@/app/lcv/components/sections/spartan/products/hero-banner/overlays';
import { getSpartanProductHeroBannerEntranceAnimation } from '@/app/lcv/components/sections/spartan/products/hero-banner/utils';
import { cn } from '@/app/utils/cn';
const CITY_ROAD_SURGE_FAIL_OPEN_MS = 800;
const ENTRANCE_SETTLE_FAIL_OPEN_MS = 2200;
export const SpartanProductHeroBannerEntrance = t0 => {
  const $ = _c(59);
  const {
    children,
    background: backgroundProp,
    mainBg,
    parallax,
    lcpMedia,
    title,
    specialLogo,
    overlay
  } = t0;
  let t1;
  if ($[0] !== parallax) {
    t1 = getSpartanProductHeroBannerEntranceAnimation(parallax);
    $[0] = parallax;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const entranceAnimation = t1;
  const hasEntranceReveal = !!entranceAnimation;
  const hasActiveDepthGlideEntrance = entranceAnimation === "active-depth-glide";
  const hasCityRoadSurgeEntrance = entranceAnimation === "city-road-surge";
  const hasLuxuryGlideInEntrance = entranceAnimation === "luxury-glide-in";
  const hasMountainSurgeEntrance = entranceAnimation === "mountain-surge";
  const hasShowroomDriveInEntrance = entranceAnimation === "showroom-drive-in";
  const hasShowroomDepthGlideEntrance = entranceAnimation === "showroom-depth-glide";
  const hasWindingRoadDriveInEntrance = entranceAnimation === "winding-road-drive-in";
  const hasPanZoomEntrance = hasActiveDepthGlideEntrance || hasCityRoadSurgeEntrance || hasLuxuryGlideInEntrance || hasMountainSurgeEntrance || hasShowroomDepthGlideEntrance || hasShowroomDriveInEntrance || hasWindingRoadDriveInEntrance;
  const [isEntranceSettled, setIsEntranceSettled] = useState(false);
  const hasCriticalMedia = lcpMedia === "background" && !!mainBg || lcpMedia === "back" && !!parallax?.back || lcpMedia === "mid" && !!parallax?.mid || lcpMedia === "front" && !!parallax?.front;
  const t2 = hasCityRoadSurgeEntrance ? CITY_ROAD_SURGE_FAIL_OPEN_MS : undefined;
  const t3 = !!parallax;
  const t4 = hasCriticalMedia && !hasCityRoadSurgeEntrance;
  let t5;
  if ($[2] !== hasEntranceReveal || $[3] !== t2 || $[4] !== t3 || $[5] !== t4) {
    t5 = {
      failOpenMs: t2,
      hasCarMedia: t3,
      hasCriticalMedia: t4,
      hasEntranceReveal
    };
    $[2] = hasEntranceReveal;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const {
    carImageRef,
    criticalImageRef,
    handleCarMediaError,
    handleCarMediaLoad,
    handleCriticalMediaError,
    handleCriticalMediaLoad,
    isReady: isEntranceReady
  } = useHeroEntranceReady(t5);
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setIsEntranceSettled(true);
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const handleEntranceSettled = t6;
  let t7;
  let t8;
  if ($[8] !== isEntranceReady || $[9] !== isEntranceSettled) {
    t7 = () => {
      if (!isEntranceReady || isEntranceSettled) {
        return;
      }
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        setIsEntranceSettled(true);
        return;
      }
      const timeoutId = window.setTimeout(handleEntranceSettled, ENTRANCE_SETTLE_FAIL_OPEN_MS);
      return () => {
        window.clearTimeout(timeoutId);
      };
    };
    t8 = [handleEntranceSettled, isEntranceReady, isEntranceSettled];
    $[8] = isEntranceReady;
    $[9] = isEntranceSettled;
    $[10] = t7;
    $[11] = t8;
  } else {
    t7 = $[10];
    t8 = $[11];
  }
  useEffect(t7, t8);
  let t9;
  if ($[12] !== backgroundProp || $[13] !== criticalImageRef || $[14] !== handleCriticalMediaError || $[15] !== handleCriticalMediaLoad || $[16] !== lcpMedia || $[17] !== mainBg || $[18] !== parallax || $[19] !== title) {
    t9 = backgroundProp ?? (mainBg ? <SpartanProductHeroBannerMainBackground title={title ?? ""} mainBg={mainBg} isLcp={lcpMedia === "background"} hasLandscapeComposition={!!parallax} imageRef={lcpMedia === "background" ? criticalImageRef : undefined} onLoad={lcpMedia === "background" ? handleCriticalMediaLoad : undefined} onError={lcpMedia === "background" ? handleCriticalMediaError : undefined} /> : undefined);
    $[12] = backgroundProp;
    $[13] = criticalImageRef;
    $[14] = handleCriticalMediaError;
    $[15] = handleCriticalMediaLoad;
    $[16] = lcpMedia;
    $[17] = mainBg;
    $[18] = parallax;
    $[19] = title;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const background = t9;
  const t10 = hasShowroomDriveInEntrance && parallax?.carEntranceDirection === "right-to-left";
  let t11;
  if ($[21] !== hasActiveDepthGlideEntrance || $[22] !== hasCityRoadSurgeEntrance || $[23] !== hasLuxuryGlideInEntrance || $[24] !== hasMountainSurgeEntrance || $[25] !== hasPanZoomEntrance || $[26] !== hasShowroomDepthGlideEntrance || $[27] !== hasShowroomDriveInEntrance || $[28] !== hasWindingRoadDriveInEntrance || $[29] !== isEntranceReady || $[30] !== t10) {
    t11 = cn(styles.entrance, {
      [styles.entranceReady]: isEntranceReady,
      [styles.entrancePanZoom]: hasPanZoomEntrance,
      [styles.entranceActiveDepthGlide]: hasActiveDepthGlideEntrance,
      [styles.entranceMountainSurge]: hasMountainSurgeEntrance,
      [styles.entranceCityRoadSurge]: hasCityRoadSurgeEntrance,
      [styles.entranceLuxuryGlideIn]: hasLuxuryGlideInEntrance,
      [styles.entranceWindingRoadDriveIn]: hasWindingRoadDriveInEntrance,
      [styles.entranceShowroomDriveIn]: hasShowroomDriveInEntrance,
      [styles.entranceShowroomDepthGlide]: hasShowroomDepthGlideEntrance,
      [styles.entranceShowroomDriveInRightToLeft]: t10
    });
    $[21] = hasActiveDepthGlideEntrance;
    $[22] = hasCityRoadSurgeEntrance;
    $[23] = hasLuxuryGlideInEntrance;
    $[24] = hasMountainSurgeEntrance;
    $[25] = hasPanZoomEntrance;
    $[26] = hasShowroomDepthGlideEntrance;
    $[27] = hasShowroomDriveInEntrance;
    $[28] = hasWindingRoadDriveInEntrance;
    $[29] = isEntranceReady;
    $[30] = t10;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== background || $[33] !== carImageRef || $[34] !== criticalImageRef || $[35] !== handleCarMediaError || $[36] !== handleCarMediaLoad || $[37] !== handleCriticalMediaError || $[38] !== handleCriticalMediaLoad || $[39] !== isEntranceReady || $[40] !== isEntranceSettled || $[41] !== lcpMedia || $[42] !== overlay || $[43] !== parallax || $[44] !== specialLogo || $[45] !== title) {
    t12 = !!parallax && <SpartanProductHeroBannerParallaxScene parallax={parallax} background={background} lcpMedia={lcpMedia} isEntranceReady={isEntranceReady} isEntranceSettled={isEntranceSettled} carImageRef={carImageRef} criticalImageRef={criticalImageRef} onCarLoad={handleCarMediaLoad} onCarError={handleCarMediaError} onCriticalLoad={handleCriticalMediaLoad} onCriticalError={handleCriticalMediaError} onEntranceSettled={handleEntranceSettled} title={title} specialLogo={specialLogo} overlay={overlay} />;
    $[32] = background;
    $[33] = carImageRef;
    $[34] = criticalImageRef;
    $[35] = handleCarMediaError;
    $[36] = handleCarMediaLoad;
    $[37] = handleCriticalMediaError;
    $[38] = handleCriticalMediaLoad;
    $[39] = isEntranceReady;
    $[40] = isEntranceSettled;
    $[41] = lcpMedia;
    $[42] = overlay;
    $[43] = parallax;
    $[44] = specialLogo;
    $[45] = title;
    $[46] = t12;
  } else {
    t12 = $[46];
  }
  let t13;
  if ($[47] !== background || $[48] !== overlay || $[49] !== parallax) {
    t13 = !parallax && <>{background}<SpartanProductHeroBannerOverlays overlay={overlay} placement="root" stack="under-environment" /><SpartanProductHeroBannerOverlays overlay={overlay} placement="root" stack="over-environment" /></>;
    $[47] = background;
    $[48] = overlay;
    $[49] = parallax;
    $[50] = t13;
  } else {
    t13 = $[50];
  }
  let t14;
  if ($[51] !== children || $[52] !== t11 || $[53] !== t12 || $[54] !== t13) {
    t14 = <div className={t11}>{t12}{t13}{children}</div>;
    $[51] = children;
    $[52] = t11;
    $[53] = t12;
    $[54] = t13;
    $[55] = t14;
  } else {
    t14 = $[55];
  }
  let t15;
  if ($[56] !== isEntranceReady || $[57] !== t14) {
    t15 = <SpartanProductHeroBannerEntranceContext.Provider value={isEntranceReady}>{t14}</SpartanProductHeroBannerEntranceContext.Provider>;
    $[56] = isEntranceReady;
    $[57] = t14;
    $[58] = t15;
  } else {
    t15 = $[58];
  }
  return t15;
};